<template>
  <div class="content relative h-full">
    <div class="flex flex-col h-full mx-4">
      <ActionsToolbar />
      <NGrid class="h-full overflow-hidden" cols="12" item-responsive>
        <NGridItem
          class="h-full overflow-auto"
          :span="
            isContentPanelOpen || (selectedSection === 'all' && (enabledFavorites || enabledRecents))
              ? '12 750:7 850:8 1400:9'
              : '12'
          "
        >
          <AllContent v-if="selectedSection === 'all'" />
          <FavoritesTable v-else-if="selectedSection === 'favorites'" />
          <RecentTable v-else-if="selectedSection === 'recent'" />
        </NGridItem>
        <NGridItem
          v-if="isContentPanelOpen || (selectedSection === 'all' && (enabledFavorites || enabledRecents))"
          class="h-full overflow-auto"
          span="0 750:5 850:4 1400:3"
        >
          <Panel v-if="isContentPanelOpen" :isLoading="apiGetters.isLoading" @close="toggleContentPanel">
            <slot v-if="!!(slots['single-file-details'] && selectedFileId)" name="single-file-details" />
            <template v-else-if="selectedFileId">
              <CFilePanel :file="apiState.selectedFile as any" is-file-browser />
              <CFileAccessManagement :file="apiState.selectedFile as any" />
            </template>
            <FolderPanel v-else-if="selectedFolderId" />
            <ItemsPanel v-else-if="selectedItemIds.length > 1" />
            <NoItemPanel v-else />
          </Panel>
          <QuickAccessPanel v-else-if="enabledFavorites || enabledRecents" />
        </NGridItem>
      </NGrid>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NGridItem, NGrid } from 'naive-ui'
import { onMounted, useSlots, watch } from 'vue'

import Panel from '@browser/components/Content/Panel/Panel.vue'
import FolderPanel from '@browser/components/Content/Panel/FolderPanel/FolderPanel.vue'
import NoItemPanel from '@browser/components/Content/Panel/NoItemPanel/NoItemPanel.vue'
import ItemsPanel from '@browser/components/Content/Panel/ItemsPanel/ItemsPanel.vue'
import AllContent from '@browser/components/Content/AllContent/AllContent.vue'
import ActionsToolbar from '@browser/components/Content/ActionsToolbar/ActionsToolbar.vue'
import FavoritesTable from '@browser/components/Content/Favorites/FavoritesTable.vue'
import RecentTable from '@browser/components/Content/Recent/RecentTable.vue'
import useAppStore from '@browser/stores/app'
import useApiStore from '@browser/stores/api'
import QuickAccessPanel from '@browser/components/Content/Panel/QuickAccessPanel.vue'
import CFilePanel from '@lib/components/CFilePanel/CFilePanel.vue'
import CFileAccessManagement from '@lib/components/CFileAccessManagement/CFileAccessManagement.vue'

const slots = useSlots()

const {
  isContentPanelOpen,
  toggleContentPanel,
  selectedFileId,
  selectedFolderId,
  selectedItemIds,
  selectedSection,
  enabledFavorites,
  enabledRecents,
} = useAppStore()
const { state: apiState, getters: apiGetters } = useApiStore()

watch(
  [selectedFileId, isContentPanelOpen],
  async () => {
    if (
      apiState.fileFetcher &&
      isContentPanelOpen.value &&
      selectedFileId.value &&
      apiState.selectedFile?.id !== selectedFileId.value
    ) {
      await apiState.fileFetcher(selectedFileId.value)
    }
  },
  { immediate: true },
)

onMounted(async () => {
  await apiState.favoritesFetcher?.()
})

watch(
  [selectedFolderId, isContentPanelOpen],
  async () => {
    if (
      apiState.folderItemsFetcher &&
      isContentPanelOpen.value &&
      selectedFolderId.value &&
      apiState.selectedFolder?.id !== selectedFolderId.value
    ) {
      await apiState.folderItemsFetcher(selectedFolderId.value, { justFetch: false, setSelectedFolder: true })
    }
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.browser__collapse-button {
  box-shadow: 0px 0px 12px rgba(41, 51, 92, 0.15);
}
</style>
